/**
 * Row-level actions for one position: duplicate, move, delete.
 *
 * The API has had all three since Phase A1 and nothing rendered them, which made a
 * project tree an append-only list -- an engineer who created a position in the wrong
 * project had no way to put it right, and duplicating a similar position (the single
 * most common way a real project gets built) meant re-keying every input.
 *
 * Two behaviours worth naming:
 *
 * - **Delete confirms first, and says what deletion means.** It is a soft delete
 *   (10.2), so the honest wording is "removed from the project", not "gone forever" --
 *   and not the reverse either.
 * - **Move lists only the projects the caller can actually write to**, so the dialog
 *   cannot offer a destination the server will refuse.
 */
import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Copy, FolderInput, MoreVertical, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useDeletePosition, useDuplicatePosition, useMovePosition, useProjects } from '@/api/hooks'
import type { Position } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'

const ITEM_CLASS = cn(
  'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
  'data-[highlighted]:bg-surface data-[highlighted]:text-text',
)

export function PositionRowActions({
  projectId,
  position,
  onChanged,
}: {
  projectId: number
  position: Position
  /** Called after a successful duplicate, move or delete. */
  onChanged?: () => void
}) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { workspaceId } = useAuth()

  const [moving, setMoving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [target, setTarget] = useState<number | null>(null)

  const duplicate = useDuplicatePosition(projectId)
  const move = useMovePosition(projectId)
  const remove = useDeletePosition(projectId)
  const { data: projects } = useProjects(workspaceId)

  // Only somewhere this position can actually go: another live project the caller may
  // edit. Offering a destination the server would refuse is a dialog that lies.
  const destinations = (projects ?? []).filter(
    (project) =>
      project.id !== projectId &&
      !project.deleted_at &&
      project.capabilities.includes('project:edit'),
  )

  const fail = (error: unknown) =>
    toast({
      title: t('common.error'),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  const onDuplicate = async () => {
    try {
      await duplicate.mutateAsync(position.id)
      toast({ title: t('position.duplicated'), variant: 'success' })
      onChanged?.()
    } catch (error) {
      fail(error)
    }
  }

  const onMove = async () => {
    if (target === null) return
    try {
      await move.mutateAsync({ positionId: position.id, targetProjectId: target })
      setMoving(false)
      setTarget(null)
      toast({ title: t('position.moved'), variant: 'success' })
      onChanged?.()
    } catch (error) {
      fail(error)
    }
  }

  const onDelete = async () => {
    try {
      await remove.mutateAsync(position.id)
      setDeleting(false)
      toast({ title: t('position.deleted'), variant: 'info' })
      onChanged?.()
    } catch (error) {
      fail(error)
    }
  }

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          aria-label={t('common.actions')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface-raised hover:text-text"
        >
          <MoreVertical size={16} aria-hidden />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-50 min-w-[12rem] rounded-md border border-border bg-surface-raised p-1 shadow-[var(--shadow-md)]"
          >
            <DropdownMenu.Item onSelect={() => void onDuplicate()} className={ITEM_CLASS}>
              <Copy size={14} aria-hidden />
              <span className="flex-1">{t('action.duplicate')}</span>
            </DropdownMenu.Item>

            <DropdownMenu.Item onSelect={() => setMoving(true)} className={ITEM_CLASS}>
              <FolderInput size={14} aria-hidden />
              <span className="flex-1">{t('position.move')}</span>
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="my-1 h-px bg-border" />

            <DropdownMenu.Item
              onSelect={() => setDeleting(true)}
              className={cn(ITEM_CLASS, 'text-[var(--status-fail)]')}
            >
              <Trash2 size={14} aria-hidden />
              <span className="flex-1">{t('action.delete')}</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Dialog open={moving} onOpenChange={setMoving}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('position.move_title')}</DialogTitle>
            <DialogDescription>{t('position.move_hint')}</DialogDescription>
          </DialogHeader>

          {destinations.length === 0 ? (
            <p className="text-sm text-text-muted">{t('position.move_empty')}</p>
          ) : (
            <fieldset className="max-h-[40dvh] space-y-2 overflow-y-auto">
              <legend className="sr-only">{t('nav.projects')}</legend>
              {destinations.map((project) => (
                <label
                  key={project.id}
                  htmlFor={`move-target-${project.id}`}
                  className="flex min-h-[2.75rem] cursor-pointer items-center gap-3 rounded-md border border-border p-3 hover:bg-surface-raised"
                >
                  <input
                    id={`move-target-${project.id}`}
                    type="radio"
                    name="move-target"
                    checked={target === project.id}
                    onChange={() => setTarget(project.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{project.name}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {[project.number, project.client].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          <DialogFooter>
            <Button type="button" onClick={() => setMoving(false)}>
              {t('action.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={move.isPending}
              disabled={target === null}
              onClick={() => void onMove()}
            >
              <FolderInput size={15} aria-hidden />
              {t('position.move')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('position.delete_title')}</DialogTitle>
            <DialogDescription>{t('position.delete_hint')}</DialogDescription>
          </DialogHeader>

          <p className="text-sm text-text">{t('position.delete_named', { name: position.name })}</p>

          <DialogFooter>
            <Button type="button" onClick={() => setDeleting(false)}>
              {t('action.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={remove.isPending}
              onClick={() => void onDelete()}
            >
              <Trash2 size={15} aria-hidden />
              {t('action.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
